import { ReactNode, useEffect, useMemo, useState } from 'react'
import * as React from 'react'
import { useRouter } from 'next/router'
import styled, { css, FlattenSimpleInterpolation } from 'styled-components'

export type TabType = {
  name: ReactNode
  size: string
  href?: {
    pathname?: string
    query?: Record<string, unknown>
  }
  id?: number
  value?: string
  gap?: string
}

type TProps = {
  data: TabType[]
  initialTab?: TabType
  className?: string
  active?: boolean
  fullWidth?: boolean
  queryKey?: string
  type?: 'query' | 'value' | 'pathname'
  onChange?: (value: any) => void
  disableGlider?: boolean
  savePrevQuery?: boolean
  checkTab?: (tab: TabType) => boolean
}

const LineTabs: React.FC<TProps> = ({
  data,
  initialTab,
  className,
  active = true,
  fullWidth = false,
  queryKey = 'tab',
  type = 'query',
  onChange = () => {},
  disableGlider = false,
  savePrevQuery = false,
  checkTab = () => true,
}) => {
  const router = useRouter()
  const [selectedTab, setSelectedTab] = useState<TabType | undefined>(
    initialTab,
  )

  useEffect(() => {
    if (!active) return

    if (type === 'query') {
      const selectedTabName = `${router.query[queryKey]}`
      let newSelectedTab = data[0]
      data.forEach((tab, idx) => {
        if (tab.href && tab.href.query?.[queryKey] === selectedTabName) {
          return (newSelectedTab = {
            ...tab,
            id: idx,
          })
        }
      })
      setSelectedTab(newSelectedTab)
    }
    if (type === 'pathname') {
      const selectedTabName = `${router.pathname}`
      let newSelectedTab = data[0]
      data.forEach((tab, idx) => {
        if (tab.href && tab.href.pathname === selectedTabName) {
          return (newSelectedTab = {
            ...tab,
            id: idx,
          })
        }
      })
      setSelectedTab(newSelectedTab)
    }
  }, [router, data])

  const handleClick = (tab: TabType) => {
    if (!checkTab(tab)) return
    if (type === 'query' || type === 'pathname') {
      let prevQuery = {}
      if (savePrevQuery) prevQuery = { ...router.query }
      router.push(
        {
          ...tab.href,
          query: {
            ...prevQuery,
            ...tab.href?.query,
          },
        },
        undefined,
        {
          scroll: false,
        },
      )
    } else {
      setSelectedTab(tab)
    }
    onChange(tab.value)
  }

  const gliderX = useMemo(() => {
    const _gliderX: string[] = ['0']

    data.reduce((prev, curr) => {
      let sum = ''

      if (fullWidth) {
        sum = `${+prev + +curr.size.slice(0, -1)}`
        _gliderX.push(`${sum}%`)
      } else {
        const gap = curr.gap?.slice(0, -2) ?? '0'
        sum = `${+prev + +curr.size.slice(0, -2) + +gap}`
        _gliderX.push(`${sum}px`)
      }
      return sum
    }, '0')

    return _gliderX
  }, [data])

  return (
    <StyledWrapper className={className} data-stretch={fullWidth}>
      {data.map((tab, idx) => (
        <Styledtab
          key={idx}
          className={`tab`}
          data-active={selectedTab?.id === tab.id}
          onClick={() => handleClick(tab)}
          customCss={css`
            width: ${tab.size};
          `}
        >
          {tab.name}
        </Styledtab>
      ))}
      {!disableGlider && selectedTab && (
        <StyledGlider
          customCss={css`
            left: ${selectedTab?.id && gliderX[selectedTab.id]};
            width: ${selectedTab.size};
          `}
        />
      )}
    </StyledWrapper>
  )
}

export default LineTabs

const StyledWrapper = styled.div<{
  customCss?: FlattenSimpleInterpolation
}>`
  display: flex;
  position: relative;
  &[data-stretch='true'] {
    width: 100%;
    .tab {
      width: 100%;
    }
  }

  ${({ customCss }) => customCss}
`

const Styledtab = styled.button<{
  customCss?: FlattenSimpleInterpolation
}>`
  cursor: pointer;
  border: none;
  background: none;
  position: relative;
  font-size: 16px;
  display: flex;
  justify-content: center;
  align-items: center;
  white-space: nowrap;
  padding: 15px;
  font-weight: 500;
  line-height: 1.5;
  letter-spacing: -0.64px;
  color: #333;
  &[data-active='true'] {
    color: #01a0ff;
    font-weight: bold;
    .count {
      background-color: #01a0ff;
    }
  }
  ${({ customCss }) => customCss}
`

const StyledGlider = styled.div<{
  customCss?: FlattenSimpleInterpolation
}>`
  position: absolute;
  height: 3px;
  background-color: #0da0ff;
  left: 0;
  bottom: 0;
  transition: all 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
  ${({ customCss }) => customCss}
`
